import React, { useEffect, useMemo, useState } from 'react';
import { identity, prop } from 'ramda';

import { Link } from '@interlayer/js/components/common/Link/Link';
import { Loader } from '@interlayer/js/components/common/Loader/Loader';

import { supportOnClickAttribute } from '@core/js/components/system/WebContent/components/WebContentInner/helpers';
import { componentService } from '@core/js/services/component.service';
import { useDeviceSelector } from '@core/js/store/hooks';
import { getClassNames } from '@core/js/utils/classNames.util';

import * as constants from '@interlayer/js/app/components/common/Button/constants';
import { NATIVE_APP_MAP } from '@interlayer/js/app/components/common/Button/constants';
import * as Types from '@interlayer/js/app/components/common/Button/types';

import style from './Button.scss';

export const Button: React.FC<Types.TButtonProps> = ({
    href,
    size = constants.SIZES.medium,
    children,
    classname,
    className,
    isLoading = false,
    disabled = false,
    buttonRef,
    type = 'button',
    view = constants.VIEWS.primary,
    status = constants.STATUSES.default,
    color = constants.COLORS.success,
    variant = constants.VARIANTS.regular,
    inNewWindow = false,
    isCenter,
    isOnlyAction,
    onClick = identity,
    isFullWidth,
    nativeApp,
    ...props
}) => {
    const os = useDeviceSelector(prop('os'));
    const [isLoadingBtn, setIsLoading] = useState(false);
    const supportAttribute: { onClick?: () => void } | null = props.onclick
        ? supportOnClickAttribute({ ...props, onClick })
        : null;
    const { VIEWS, VARIANTS, COLORS, SIZES, STATUSES } = constants;
    const classNames = isOnlyAction ? style.clearBtn : getClassNames([
        className,
        classname,
        style.btn,
        style[SIZES[size]],
        style[STATUSES[status]],
        style[VIEWS[view]],
        style[COLORS[color]],
        style[VARIANTS[variant]],
        disabled ? style.disabled : '',
        isCenter ? style.isCenter : '',
        isFullWidth ? style.fullWidth : ''
    ]);
    const nativeAppUrl = useMemo(() => {
        if (!nativeApp) {
            return '';
        }

        switch (os) {
            case 'android':
                return `https://play.google.com/store/apps/details?id=${nativeApp === 'bingo' ? NATIVE_APP_MAP.androidBingo : NATIVE_APP_MAP.androidCasino}`;

            case 'ios':
                return `itms-apps://itunes.apple.com/app/id${nativeApp === 'bingo' ? NATIVE_APP_MAP.iosBingo : NATIVE_APP_MAP.iosCasino}`;

            default:
                return '';
        }
    }, [nativeApp, os]);

    useEffect(() => {
        let interval: ReturnType<typeof setTimeout> | undefined;

        if (isLoading) {
            interval = setTimeout(() => {
                setIsLoading(true);
            }, constants.PRE_SCREENING_DELAY);
        } else {
            setIsLoading(false);
        }

        return () => clearInterval(interval);
    }, [isLoading]);

    if (nativeApp && !nativeAppUrl) {
        return null;
    }

    return href || nativeAppUrl
        ? (
            <Link
                data-testid="Button_link"
                href={href || nativeAppUrl}
                inNewWindow={inNewWindow}
                className={classNames}
                {...props}
            >
                {children}
            </Link>
        )
        : (
            <button
                data-testid="Button_btn"
                type={type}
                disabled={disabled}
                className={classNames}
                onClick={supportAttribute?.onClick || onClick}
                {...buttonRef && { ref: buttonRef }}
                {...props}
            >
                {isLoadingBtn
                    ? <Loader theme="navy" className={style.loader} />
                    : children
                }
            </button>
        );
};

componentService.registerWebContentComponents({ Button });
